import { Box, Card, CardContent, Container, Typography } from '@mui/material'
import React from 'react'

import { TranslationFunction } from '@/src/types/translations'
import { AccessibleOutlined, DateRangeOutlined, SupervisorAccountOutlined } from '@mui/icons-material'

import { RecentSearchListItem } from '@/src/hooks/srp/recentSearch'
import { convertToUSDateFormat } from '@/src/utils/date/convertDateFormat'

interface RecentSearchListProps {
    formatDate: (date: string, format: string) => string
    flightsSearchList: (RecentSearchListItem & { onClick?: () => void })[]
    t: TranslationFunction<'homePage.headings'>
    tCommon: TranslationFunction<'common'>
}

const RecentSearchList: React.FC<RecentSearchListProps> = ({ formatDate, flightsSearchList, t, tCommon }) => {
    return (
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Container maxWidth="xl" sx={{ width: '100%', minHeight: '100%' }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                    {t('recentSearches')}
                </Typography>

                <Box
                    component="ul"
                    sx={{
                        display: 'flex',
                        gap: 1.25,
                        listStyle: 'none',
                        p: 0,
                        m: 0,
                        overflowX: 'auto',
                        pb: 1,
                        pt: 3,
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap'
                    }}
                >
                    {flightsSearchList.map((item, index) => {
                        if (!item) return null

                        const adults = item.adults || 0
                        const childs = item.childs || 0
                        const infants = item.infants || 0
                        const totalTraveler = item.numberOfPax || adults + childs + infants
                        const isSinglePerson = totalTraveler < 2
                        const tripType = item.returnDate ? tCommon('roundTrip') : tCommon('oneWay')
                        const travelClass = item?.flightClass ? tCommon(item.flightClass as any) : ''
                        const departDate = formatDate(convertToUSDateFormat(item.departDate), 'DD MMM, YYYY')
                        const returnDate = item.returnDate
                            ? formatDate(convertToUSDateFormat(item.returnDate), 'DD MMM, YYYY')
                            : ''

                        return (
                            <Box
                                key={`flight-search-${index}`}
                                component="li"
                                sx={{
                                    minWidth: 325,
                                    flex: '0 0 auto'
                                }}
                            >
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        position: 'relative',
                                        boxShadow: 'rgba(0, 0, 0, 0.04) 0px 8px 16px',
                                        border: '1px solid #e6e6e6',
                                        '&:hover': {
                                            boxShadow: 'rgba(0, 0, 0, 0.08) 0px 12px 20px',
                                            transform: 'translateY(-2px)',
                                            transition: 'all 0.2s ease-in-out'
                                        }
                                    }}
                                    onClick={item.onClick}
                                >
                                    <CardContent sx={{ p: 2, pt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {tripType}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.primary',
                                                fontWeight: 500,
                                                mb: 1
                                            }}
                                        >
                                            {item.fromCity && item.toCity
                                                ? `${item.fromCity} → ${item.toCity}`
                                                : `${item.from} → ${item.to}`}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                            sx={{ mb: 1, gap: '2px', display: 'flex', alignItems: 'center' }}
                                        >
                                            <DateRangeOutlined sx={{ fontSize: 16 }} />{' '}
                                            {`${departDate}${returnDate ? ` - ${returnDate}` : ''}`}{' '}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                            sx={{ mb: 1, gap: '2px', display: 'flex', alignItems: 'center' }}
                                        >
                                            <SupervisorAccountOutlined sx={{ fontSize: 16 }} />{' '}
                                            {`${totalTraveler} ${tCommon('traveller', { s: isSinglePerson ? '' : 's' })}`}
                                            <AccessibleOutlined sx={{ fontSize: 16, ml: 0.5 }} />
                                            {`${travelClass}`}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        )
                    })}
                </Box>
            </Container>
        </Box>
    )
}

export { RecentSearchList }
export type { RecentSearchListProps }
